/**
 * Wire vocabulary of the AI support chat (SSE protocol v1 + JSON contract).
 *
 * Kept identical to the backend contract (backend plan §4). Every enum is
 * frozen so a typo'd mutation fails loudly instead of silently changing the
 * protocol. Framework-free: this folder is copied 1:1 to next-mapit as TS.
 */

/** SSE protocol version the client speaks; the server echoes it in `meta.v`. */
export const PROTOCOL_VERSION = 1;

/** SSE `event:` names sent by `POST /ai-support/chat`. */
export const SSE_EVENTS = Object.freeze({
  META: "meta",
  DELTA: "delta",
  DONE: "done",
  ERROR: "error",
});

/**
 * App error codes. 851–858 arrive in the JSON envelope `status` of a non-2xx
 * response (before the stream starts); an SSE `error` event carries 859, or
 * 852 when no model profile could answer. `network` and `parse` are
 * client-side codes for transport failures.
 */
export const ERROR_CODES = Object.freeze({
  NOT_ELIGIBLE: 851,
  UNAVAILABLE: 852,
  RATE_LIMITED: 853,
  STREAM_IN_PROGRESS: 854,
  CONVERSATION_NOT_FOUND: 855,
  CONVERSATION_CLOSED: 856,
  MESSAGE_TOO_LONG: 857,
  DUPLICATE_MESSAGE: 858,
  PROVIDER_ERROR: 859,
  NETWORK: "network",
  PARSE: "parse",
});

/** `Message.status` as returned by the server. */
export const MESSAGE_STATUS = Object.freeze({
  COMPLETE: "complete",
  STREAMING: "streaming",
  ABORTED: "aborted",
  FAILED: "failed",
  INTERRUPTED: "interrupted",
});

/** `Message.role`. */
export const MESSAGE_ROLES = Object.freeze({
  USER: "user",
  ASSISTANT: "assistant",
});

/** `escalation.reason` on a `done` event or a stored assistant message. */
export const ESCALATION_REASONS = Object.freeze({
  NOT_FOUND: "not_found",
  UNCONFIRMED: "unconfirmed",
  FINANCIAL: "financial",
  SENSITIVE: "sensitive",
  CARRIER: "carrier",
  HUMAN_REQUESTED: "human_requested",
});

/** `Conversation.status`. */
export const CONVERSATION_STATUS = Object.freeze({
  OPEN: "open",
  CLOSED: "closed",
});

/** `Conversation.closedReason` (null while the conversation is open). */
export const CLOSED_REASONS = Object.freeze({
  NEW_CONVERSATION: "new_conversation",
  IDLE: "idle",
});

/** `done.finishReason`. `length` still means a complete (truncated) answer. */
export const FINISH_REASONS = Object.freeze({
  STOP: "stop",
  LENGTH: "length",
});

/** `POST /messages/:id/feedback` `rating`. */
export const FEEDBACK_RATINGS = Object.freeze({
  UP: "up",
  DOWN: "down",
});
